import { useQueryClient } from '@tanstack/react-query'
import { useRoutingContext } from 'app/routing_context'
import { refetchMessagesIfNotExist } from 'chat/messages/queries/list'
import { MessagesScreenParams } from 'chat/messages/types'
import * as Notifications from 'expo-notifications'
import {
  Notification as ExpoNotification,
  useLastNotificationResponse,
} from 'expo-notifications'
import { HomeStackParamList } from 'home/stack/types'
import React, { createContext, useEffect, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { useUserBadgeCount } from 'users/queries/get_badge'
import { getPushNotificationInfo } from './push'
import { useMarkPushNotificationRead } from './queries/mark_push_read'

const PushNotificationsContext = createContext({})
interface Props {
  children: React.ReactNode
}

// Empty implementation for web
function MockPushNotificationsProvider({ children }: Props) {
  return (
    <PushNotificationsContext.Provider value={{}}>
      {children}
    </PushNotificationsContext.Provider>
  )
}

// Full Native implementation
function NativePushNotificationsProvider({ children }: Props) {
  const client = useQueryClient()
  const { handleNavigation } = useRoutingContext()
  const pushNotificationResponse = useLastNotificationResponse()
  const [lastRoutedPushNotification, setLastRoutedPushNotification] = useState<
    ExpoNotification | undefined
  >()
  const markNotificationReadMutation = useMarkPushNotificationRead()
  const appState = useRef(AppState.currentState)
  const { refetch: refetchBadgeCount } = useUserBadgeCount()

  // Handle push notification responses
  useEffect(() => {
    if (
      !pushNotificationResponse ||
      lastRoutedPushNotification?.date ===
        pushNotificationResponse.notification.date
    ) {
      return
    }

    const pushNotificationInfo = getPushNotificationInfo(
      pushNotificationResponse
    )

    if (!pushNotificationInfo) {
      return
    }

    setLastRoutedPushNotification(pushNotificationResponse.notification)

    if (pushNotificationInfo.markAsReadAction) {
      markNotificationReadMutation.mutate({ pushNotificationInfo })
    }

    if (pushNotificationInfo.route.name === 'messages') {
      const messageId = pushNotificationInfo.route.params?.messageId
      refetchMessagesIfNotExist(client, messageId).catch(e => console.log(e))

      if (pushNotificationInfo.route.params) {
        handleNavigation({
          routeName: 'messages',
          routeParams: {
            ...(pushNotificationInfo.route.params as MessagesScreenParams),
            showLoading: true,
          },
          resetNavigationState: true,
        })
      }
    } else {
      handleNavigation({
        routeName: pushNotificationInfo.route.name as keyof HomeStackParamList,
        routeParams: pushNotificationInfo.route.params && {
          ...pushNotificationInfo.route.params,
        },
      })
    }
  }, [
    client,
    handleNavigation,
    lastRoutedPushNotification?.date,
    markNotificationReadMutation,
    pushNotificationResponse,
  ])

  // Handle app state changes for badge count updates
  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (nextAppState === 'active') {
        // Refetch the badge count to ensure we get the latest value from the server
        refetchBadgeCount()
          .then(({ data }) => {
            if (data !== undefined) {
              Notifications.setBadgeCountAsync(data).catch(err =>
                console.error('Error setting badge count ', err)
              )
            }
          })
          .catch(err => console.error('Error refetching badge count ', err))
      }
      appState.current = nextAppState
    })

    return () => {
      subscription.remove()
    }
  }, [refetchBadgeCount])

  return (
    <PushNotificationsContext.Provider value={{}}>
      {children}
    </PushNotificationsContext.Provider>
  )
}
export function PushNotificationsProvider(props: Props) {
  if (Platform.OS === 'web') {
    return <MockPushNotificationsProvider {...props} />
  }

  return <NativePushNotificationsProvider {...props} />
}
